import { webApiClient } from '../authentication';

interface ProductEntry {
  sku?: string | null;
  priceInCents?: number | null;
  activationFeeInCents?: number | null;
  pricingInCents?: Record<string, number> | null;
}

interface ShippingOptionEntry {
  code?: string | null;
  pricingInCents?: Record<string, number> | null;
  priceInCents?: number | null;
  isExpedited?: boolean | null;
  exchangeOnly?: boolean | null;
  inPersonEventOnly?: boolean | null;
}

interface ProductsResponse {
  products: ProductEntry[];
  shippingOptions?: ShippingOptionEntry[];
}

export interface PriceLine {
  label: string;
  amountInCents: number;
  /** Rendered as a deduction rather than a charge. */
  isCredit?: boolean;
  /** Rendered as "Included" rather than an amount. */
  isIncluded?: boolean;
}

export interface PriceBreakdown {
  lines: PriceLine[];
  totalInCents: number;
  /**
   * The shipping method the order is placed with. Rides along here because it comes from the same
   * products response, and because the purchase is rejected as unfulfillable without one -- this flow
   * has no shipping selector, so something has to choose.
   */
  shippingCode: string | null;
}

/**
 * Builds the bag's itemised breakdown from the plan's own server-side pricing.
 *
 * Both figures come from the plan record: `pricingInCents.USD` is the membership price and
 * `activationFeeInCents` is what is actually charged today. Nothing is hardcoded, so the screen cannot
 * drift from the catalogue.
 *
 * The credit equals the full membership price because that is definitionally what this offer is -- six
 * months free, backed by a fixed-amount coupon worth the whole 6-month term. If the offer ever becomes
 * a partial discount, this has to start reading the coupon's own amount instead of inferring it.
 *
 * Deliberately NOT sourced from `/api/promotions/code-details`. That endpoint exists to power per-plan
 * "save $X" badges and is the wrong shape for this: `getCodeDetails` looks the code up directly and
 * never runs `resolvePromoAlias`, so a GWA alias can never resolve there — alias resolution needs the
 * signed entitlement the cart carries, which a GET has no access to. It also returns empty arrays
 * whenever its own subscription-plan fetch comes back empty, which was observed against a local
 * backend during a Recurly subscription-API incident; the plan catalogue used here kept working
 * throughout, so this path also survives that class of outage.
 */
export function buildPriceBreakdown(
  products: ProductEntry[],
  planSku: string,
  shippingOptions: ShippingOptionEntry[] = [],
): PriceBreakdown | null {
  const plan = products.find((product) => product.sku === planSku);
  const membershipInCents = plan?.pricingInCents?.USD ?? plan?.priceInCents ?? 0;
  if (!plan || membershipInCents <= 0) {
    return null;
  }

  const activationInCents = plan.activationFeeInCents ?? 0;

  const lines: PriceLine[] = [
    { label: 'Fi Mini GPS tracker', amountInCents: 0, isIncluded: true },
    { label: '6 months of membership', amountInCents: membershipInCents },
    { label: 'Fi Nano registration credit', amountInCents: membershipInCents, isCredit: true },
  ];
  if (activationInCents > 0) {
    lines.push({ label: 'Activation fee', amountInCents: activationInCents });
  }

  return {
    lines,
    totalInCents: activationInCents,
    shippingCode: cheapestOrdinaryShippingCode(shippingOptions),
  };
}

/** Fetches the catalogue and builds the breakdown. Null when the offer's plan is absent from it. */
export async function fetchPriceBreakdown(planSku: string): Promise<PriceBreakdown | null> {
  try {
    const { data } = await webApiClient.get<ProductsResponse>('/api/ecommerce/products');
    return buildPriceBreakdown(data.products ?? [], planSku, data.shippingOptions ?? []);
  } catch {
    return null;
  }
}

/**
 * The cheapest ordinary shipping method.
 *
 * Chosen rather than hardcoded so a renamed or repriced option follows the catalogue. Expedited,
 * exchange-only and in-person-event options are excluded: the first two are not what this offer sells,
 * and `so-in_person` is $0 but only valid at an event, so picking on price alone would select it.
 */
function cheapestOrdinaryShippingCode(options: ShippingOptionEntry[]): string | null {
  const eligible = options
    .filter((option) => !!option.code && !option.isExpedited && !option.exchangeOnly && !option.inPersonEventOnly)
    .map((option) => ({
      code: option.code as string,
      priceInCents: option.pricingInCents?.USD ?? option.priceInCents ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.priceInCents - b.priceInCents);

  return eligible[0]?.code ?? null;
}

/** Cents to a plain dollar string. Whole dollars drop the decimals, matching the prototype. */
export function formatCents(amountInCents: number): string {
  const dollars = amountInCents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
}
